/**
 * CAD Upload API
 * Handles file upload and unauthenticated WebSocket connection
 */

import {
  getAPIBaseURL,
  getWebSocketBaseURL,
  POLL_INTERVAL_MS,
  POLL_VIEWER_TIMEOUT_MS,
  POLL_ANALYSIS_TIMEOUT_MS,
} from '../utils/constants'
import {
  type TempRequestResponse,
  type TempRequestCookiePayload,
  type ArticleReference,
  type FileReference,
  type PropertyReference,
} from '../utils/types'
import LogTool from '../../../logger/logTools'

const log = new LogTool({ context: 'CADUploadAPI', enable: true, logLevel: 'debug' })

/**
 * Upload a CAD file to create a temporary request
 */
export async function uploadCADFile(
  file: File,
  quantity = 1
): Promise<TempRequestResponse> {
  const formData = new FormData()
  formData.append('quantity', String(quantity))
  formData.append('accept_privacy_policy', 'True')
  formData.append('document', file)

  const apiUrl = getAPIBaseURL()
  const response = await fetch(`${apiUrl}v1/request/aqg_upload/`, {
    method: 'POST',
    body: formData,
  })

  if (!response.ok) {
    const errorText = await response.text()
    log.error('Upload failed:', response.status, errorText)
    throw new Error(`Upload failed with status ${response.status}`)
  }

  const data = await response.json()
  log.info('Upload successful:', data)
  return data as TempRequestResponse
}

/**
 * Set the access secret cookie for WebSocket authentication
 */
export function setAccessSecretCookie(accessSecret: string): void {
  const hostname = window.location.hostname
  const maxAge = 60 * 60 * 24 * 14 // 14 days
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  
  if (isLocalhost) {
    // For localhost: no Secure flag (HTTP), no Domain restriction
    document.cookie = `access_secret=${accessSecret}; Path=/; Max-Age=${maxAge}; SameSite=Lax`
  } else {
    // For production: cross-domain with Secure flag (HTTPS required)
    const domain = hostname.includes('assemblean') ? '.assemblean.com' : hostname
    document.cookie = `access_secret=${accessSecret}; Path=/; Domain=${domain}; Max-Age=${maxAge}; SameSite=None; Secure`
  }
}

/**
 * Set the temp request instance cookie for GuestRequestPage
 */
export function setTempRequestCookie(tempRequest: TempRequestCookiePayload): void {
  const hostname = window.location.hostname
  const maxAge = 60 * 60 * 24 * 14 // 14 days
  const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
  const cookiePayload = {
    self: tempRequest.self,
  }
  
  if (isLocalhost) {
    // For localhost: no Secure flag (HTTP), use Lax since None requires Secure
    document.cookie = `tempRequestInst=${JSON.stringify(cookiePayload)}; Path=/; Max-Age=${maxAge}; SameSite=Lax`
  } else {
    // For production: cross-domain with Secure flag (HTTPS required)
    // SameSite=None is required for cross-site iframe contexts
    const domain = hostname.includes('assemblean') ? '.assemblean.com' : hostname
    document.cookie = `tempRequestInst=${JSON.stringify(cookiePayload)}; Path=/; Domain=${domain}; Max-Age=${maxAge}; SameSite=None; Secure`
  }
}

/**
 * Create an unauthenticated WebSocket connection
 * The access_secret cookie must be set before calling this
 */
export function createUnauthWebSocket(accessSecret: string): WebSocket {
  const wsUrl = getWebSocketBaseURL()
  // Pass access_secret as query parameter for cross-origin support
  const fullUrl = `${wsUrl}unauth_socket/?access_secret=${encodeURIComponent(accessSecret)}`
  const socket = new WebSocket(fullUrl)
  
  log.info('Creating unauthenticated WebSocket connection')
  return socket
}

/**
 * Fetch article with expanded fields
 */
export async function fetchArticle(
  articleUrl: string,
  accessSecret: string,
  expand: string[] = ['files', 'properties']
): Promise<ArticleReference> {
  const expandParam = expand.length > 0 ? `?expand=${expand.join(',')}` : ''
  const url = `${articleUrl}${expandParam}`

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'X-ACCESS-SECRET': accessSecret,
    },
  })

  if (!response.ok) {
    throw new Error(`Failed to fetch article: ${response.status}`)
  }

  return response.json()
}

/**
 * Check if SCS file is available in the article files
 */
export function hasSCSFile(files: FileReference[]): boolean {
  return files.some((f) => f.name?.toLowerCase().includes('.scs'))
}

/**
 * Get the SCS file from article files
 */
export function getSCSFile(files: FileReference[]): FileReference | undefined {
  return files.find((f) => f.name?.toLowerCase().includes('.scs'))
}

/**
 * Check if estimated price is available in properties
 */
export function hasPriceAvailable(properties: PropertyReference[]): boolean {
  const estimatedPrice = properties.find((p) => p.name === 'estimated_price')
  return estimatedPrice !== undefined &&
         estimatedPrice.value !== '' &&
         estimatedPrice.value !== 'loading' &&
         !estimatedPrice.value.toLowerCase().startsWith('error:')
}

export function getConversionError(properties: PropertyReference[]): string | null {
  const conversionError = properties.find((p) => p.name === 'conversion_error')
  if (conversionError?.value) {
    return conversionError.value.trim()
  }

  const estimatedPrice = properties.find((p) => p.name === 'estimated_price')
  const estimatedValue = estimatedPrice?.value
  if (estimatedValue && estimatedValue.toLowerCase().startsWith('error:')) {
    return estimatedValue.slice('error:'.length).trim() || 'unknown'
  }

  return null
}

interface PollOptions {
  onSuccess: () => void
  onError: (error: Error) => void
  signal?: AbortSignal
}

/**
 * Poll for SCS file availability (3D viewer preparation)
 */
export function pollForSCSFile(
  articleUrl: string,
  accessSecret: string,
  options: PollOptions
): void {
  const startTime = Date.now()
  const { onSuccess, onError, signal } = options

  const poll = async (): Promise<void> => {
    if (signal?.aborted) {
      return
    }

    const elapsed = Date.now() - startTime
    if (elapsed > POLL_VIEWER_TIMEOUT_MS) {
      onError(new Error('Timeout waiting for 3D viewer preparation'))
      return
    }

    try {
      const article = await fetchArticle(articleUrl, accessSecret, ['files'])
      const files = article.files

      if (Array.isArray(files) && hasSCSFile(files)) {
        log.info('SCS file available')
        onSuccess()
        return
      }

      // Keep polling
      setTimeout(() => void poll(), POLL_INTERVAL_MS)
    } catch (error) {
      log.error('Error polling for SCS file:', error)
      onError(error instanceof Error ? error : new Error('Unknown error'))
    }
  }

  void poll()
}

/**
 * Poll for price analysis completion
 */
export function pollForPriceAnalysis(
  articleUrl: string,
  accessSecret: string,
  options: PollOptions
): void {
  const startTime = Date.now()
  const { onSuccess, onError, signal } = options

  const poll = async (): Promise<void> => {
    if (signal?.aborted) {
      return
    }

    const elapsed = Date.now() - startTime
    if (elapsed > POLL_ANALYSIS_TIMEOUT_MS) {
      onError(new Error('Timeout waiting for price analysis'))
      return
    }

    try {
      const article = await fetchArticle(articleUrl, accessSecret, ['properties'])
      const properties = article.properties

      if (Array.isArray(properties)) {
        const conversionError = getConversionError(properties)
        if (conversionError) {
          onError(new Error(`conversionError:${conversionError}`))
          return
        }

        if (hasPriceAvailable(properties)) {
          log.info('Price analysis complete')
          onSuccess()
          return
        }
      }

      // Keep polling
      setTimeout(() => void poll(), POLL_INTERVAL_MS)
    } catch (error) {
      log.error('Error polling for price analysis:', error)
      onError(error instanceof Error ? error : new Error('Unknown error'))
    }
  }

  void poll()
}
